import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, ShieldAlert, KeyRound, Power, PowerOff, Sparkles, Copy, Mail, Check as CheckIcon } from "lucide-react";
import PasswordStrength, { isPasswordStrong, suggestPassword } from "@/components/PasswordStrength";

const ROLE_LABEL = { admin: "Administrator", user: "Benutzer", guest: "Gast" };
const EMPTY_FORM = { email: "", password: "", name: "", role: "user", allowed_tenants: [], must_change_password: true };

const APP_URL = (process.env.REACT_APP_APP_URL || "https://lagerbewertung.emergent.host").replace(/\/$/, "");

function formatLastLogin(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
    } catch {
        return "—";
    }
}

export default function Benutzer() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState(EMPTY_FORM);

    const [editOpen, setEditOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", role: "user", password: "", allowed_tenants: [] });

    const [deleteTarget, setDeleteTarget] = useState(null);
    // Post-Create-Dialog: Zeigt Zugangsdaten einmalig zum Kopieren/Mailen an
    const [credsDialog, setCredsDialog] = useState({ open: false, name: "", email: "", password: "", mustChange: true });

    const isAdmin = currentUser?.role === "admin";

    const load = async () => {
        setLoading(true);
        try {
            const [u, t] = await Promise.all([api.get("/users"), api.get("/tenants")]);
            setUsers(u.data);
            setTenants(t.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Benutzer konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin) load();
    }, [isAdmin]);

    const submitCreate = async () => {
        if (!isPasswordStrong(createForm.password)) {
            toast.error("Passwort erfüllt die Regeln nicht");
            return;
        }
        try {
            await api.post("/users", createForm);
            toast.success("Benutzer angelegt");
            // Zugangsdaten-Dialog zeigen, BEVOR das Formular geleert wird
            setCredsDialog({
                open: true,
                name: createForm.name,
                email: createForm.email,
                password: createForm.password,
                mustChange: !!createForm.must_change_password,
            });
            setCreateOpen(false);
            setCreateForm(EMPTY_FORM);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
        }
    };

    const openEdit = (u) => {
        setEditTarget(u);
        setEditForm({
            name: u.name,
            role: u.role,
            password: "",
            allowed_tenants: u.allowed_tenants || [],
        });
        setEditOpen(true);
    };

    const submitEdit = async () => {
        if (editForm.password && !isPasswordStrong(editForm.password)) {
            toast.error("Neues Passwort erfüllt die Regeln nicht");
            return;
        }
        try {
            const payload = {
                name: editForm.name,
                role: editForm.role,
                allowed_tenants: editForm.allowed_tenants,
            };
            if (editForm.password) payload.password = editForm.password;
            await api.put(`/users/${editTarget.user_id}`, payload);
            toast.success("Benutzer aktualisiert");
            if (editForm.password) {
                setCredsDialog({
                    open: true,
                    name: editForm.name,
                    email: editTarget.email,
                    password: editForm.password,
                    mustChange: true,
                });
            }
            setEditOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen");
        }
    };

    const toggleCreateTenant = (tid) => {
        setCreateForm((f) => ({
            ...f,
            allowed_tenants: f.allowed_tenants.includes(tid)
                ? f.allowed_tenants.filter((x) => x !== tid)
                : [...f.allowed_tenants, tid],
        }));
    };

    const toggleEditTenant = (tid) => {
        setEditForm((f) => ({
            ...f,
            allowed_tenants: f.allowed_tenants.includes(tid)
                ? f.allowed_tenants.filter((x) => x !== tid)
                : [...f.allowed_tenants, tid],
        }));
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/users/${deleteTarget.user_id}`);
            toast.success("Benutzer gelöscht");
            setDeleteTarget(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };

    const toggleActive = async (u) => {
        const nextActive = !(u.is_active !== false);
        try {
            await api.patch(`/users/${u.user_id}/status`, { is_active: nextActive });
            toast.success(nextActive ? "Benutzer aktiviert" : "Benutzer deaktiviert");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Statuswechsel fehlgeschlagen");
        }
    };

    if (!isAdmin) {
        return (
            <div className="max-w-2xl mx-auto mt-20 text-center animate-fade-up">
                <ShieldAlert className="h-12 w-12 mx-auto text-yellow-400" />
                <h2 className="font-heading text-2xl mt-4">Keine Berechtigung</h2>
                <p className="text-muted-foreground mt-2">
                    Die Benutzerverwaltung ist ausschließlich für Administratoren zugänglich.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Verwaltung</div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">
                        Benutzerverwaltung
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Anlegen, Bearbeiten und Löschen von Benutzerkonten und deren Rollen.
                    </p>
                </div>
                <Button
                    onClick={() => {
                        setCreateForm(EMPTY_FORM);
                        setCreateOpen(true);
                    }}
                    className="bg-tenant text-[color:var(--tenant-accent-foreground)] hover:opacity-90"
                    data-testid="user-create-btn"
                >
                    <Plus className="h-4 w-4 mr-2" /> Neuer Benutzer
                </Button>
            </div>

            <Card className="p-5 bg-surface1 border-border">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
                    <Users className="h-3.5 w-3.5" /> Alle Benutzer ({users.length})
                </div>
                <div className="overflow-auto border border-border rounded-md">
                    <Table>
                        <TableHeader className="bg-surface2 sticky top-0">
                            <TableRow>
                                <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                                <TableHead className="text-xs uppercase tracking-widest">E-Mail</TableHead>
                                <TableHead className="text-xs uppercase tracking-widest">Rolle</TableHead>
                                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                                <TableHead className="text-xs uppercase tracking-widest">Zuletzt eingeloggt</TableHead>
                                <TableHead className="text-xs uppercase tracking-widest">Verlage</TableHead>
                                <TableHead className="text-xs uppercase tracking-widest text-right">Aktionen</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-6 text-sm text-muted-foreground">
                                        Lade…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && users.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-6 text-sm text-muted-foreground">
                                        Keine Benutzer vorhanden.
                                    </TableCell>
                                </TableRow>
                            )}
                            {users.map((u) => {
                                const isSelf = u.user_id === currentUser?.user_id;
                                const isActive = u.is_active !== false;
                                return (
                                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`} className={!isActive ? "opacity-60" : ""}>
                                        <TableCell className="font-medium">
                                            {u.name}
                                            {isSelf && (
                                                <Badge variant="secondary" className="ml-2 text-[10px]">
                                                    Sie
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                                        <TableCell>
                                            <Badge className="bg-tenant text-[color:var(--tenant-accent-foreground)]">
                                                {ROLE_LABEL[u.role] || u.role}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {isActive ? (
                                                <Badge variant="outline" className="text-[10px] border-emerald-500/50 text-emerald-300 bg-emerald-500/10">
                                                    Aktiv
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[10px] border-red-500/50 text-red-300 bg-red-500/10">
                                                    Deaktiviert
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                            <div>{formatLastLogin(u.last_login_at)}</div>
                                            {u.login_count > 0 && (
                                                <div className="text-[10px] opacity-60">{u.login_count}× Login</div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {u.role === "admin" ? (
                                                <span className="text-xs text-muted-foreground italic">
                                                    Alle Verlage
                                                </span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1">
                                                    {(u.allowed_tenants || []).length === 0 && (
                                                        <span className="text-xs text-yellow-400/80">
                                                            Keine zugewiesen
                                                        </span>
                                                    )}
                                                    {(u.allowed_tenants || []).map((tid) => {
                                                        const t = tenants.find((x) => x.tenant_id === tid);
                                                        return (
                                                            <Badge
                                                                key={tid}
                                                                variant="outline"
                                                                className="text-[10px]"
                                                                title={tid}
                                                            >
                                                                {t?.kurzform || t?.name || tid}
                                                            </Badge>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => toggleActive(u)}
                                                    disabled={isSelf}
                                                    data-testid={`user-toggle-active-${u.user_id}`}
                                                    title={isActive ? "Deaktivieren (Login sperren)" : "Reaktivieren"}
                                                    className={isActive ? "text-emerald-400 hover:text-emerald-300" : "text-red-400 hover:text-red-300"}
                                                >
                                                    {isActive ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => openEdit(u)}
                                                    data-testid={`user-edit-${u.user_id}`}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => setDeleteTarget(u)}
                                                    disabled={isSelf}
                                                    data-testid={`user-delete-${u.user_id}`}
                                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Neuen Benutzer anlegen</DialogTitle>
                        <DialogDescription>Erstellen Sie ein Benutzerkonto mit E-Mail-Login.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Name</Label>
                            <Input
                                value={createForm.name}
                                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                placeholder="Max Mustermann"
                                data-testid="user-create-name"
                            />
                        </div>
                        <div>
                            <Label>E-Mail</Label>
                            <Input
                                type="email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                placeholder="name@verlag.at"
                                data-testid="user-create-email"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label>Initialpasswort</Label>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setCreateForm({ ...createForm, password: suggestPassword() })}
                                    data-testid="user-create-suggest-pw"
                                    className="h-7 px-2 text-[11px] gap-1"
                                >
                                    <Sparkles className="h-3 w-3" /> Vorschlag generieren
                                </Button>
                            </div>
                            <Input
                                type="text"
                                value={createForm.password}
                                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                placeholder="oder Vorschlag verwenden"
                                data-testid="user-create-password"
                                autoComplete="off"
                                className="font-mono"
                            />
                            <PasswordStrength pw={createForm.password} />
                        </div>
                        <div>
                            <Label>Rolle</Label>
                            <select
                                value={createForm.role}
                                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                                className="mt-1 h-10 w-full px-3 rounded-md border border-border bg-surface1 text-sm"
                                data-testid="user-create-role"
                            >
                                <option value="admin">Administrator</option>
                                <option value="user">Benutzer</option>
                                <option value="guest">Gast</option>
                            </select>
                        </div>
                        {createForm.role !== "admin" && (
                            <div>
                                <Label>Verlage</Label>
                                <p className="text-[11px] text-muted-foreground mb-2">
                                    Mindestens einen Verlag auswählen, den dieser Benutzer einsehen darf.
                                </p>
                                <div className="space-y-2">
                                    {tenants.map((t) => (
                                        <label
                                            key={t.tenant_id}
                                            className="flex items-center gap-2 cursor-pointer"
                                            data-testid={`user-create-tenant-${t.tenant_id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={createForm.allowed_tenants.includes(t.tenant_id)}
                                                onChange={() => toggleCreateTenant(t.tenant_id)}
                                                className="h-4 w-4 accent-current"
                                                style={{ accentColor: t.farbe }}
                                            />
                                            <span className="text-sm">{t.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        {createForm.role === "admin" && (
                            <div className="text-xs text-muted-foreground italic border-l-2 border-tenant pl-3">
                                Administratoren haben automatisch Zugriff auf alle Verlage.
                            </div>
                        )}
                        <label className="flex items-start gap-2 cursor-pointer text-sm bg-surface2 border border-border rounded-md px-3 py-2">
                            <input
                                type="checkbox"
                                checked={createForm.must_change_password}
                                onChange={(e) => setCreateForm({ ...createForm, must_change_password: e.target.checked })}
                                className="h-4 w-4 mt-0.5"
                                data-testid="user-create-must-change"
                            />
                            <span>
                                <span className="font-medium">Muss beim ersten Login geändert werden</span>
                                <span className="block text-[11px] text-muted-foreground mt-0.5">
                                    Empfohlen: der Nutzer setzt beim ersten Login selbst ein Passwort.
                                </span>
                            </span>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={submitCreate}
                            disabled={
                                !createForm.email ||
                                !isPasswordStrong(createForm.password) ||
                                !createForm.name ||
                                (createForm.role !== "admin" && createForm.allowed_tenants.length === 0)
                            }
                            className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                            data-testid="user-create-submit"
                        >
                            Anlegen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Benutzer bearbeiten</DialogTitle>
                        <DialogDescription>
                            {editTarget?.email}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Name</Label>
                            <Input
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                data-testid="user-edit-name"
                            />
                        </div>
                        <div>
                            <Label>Rolle</Label>
                            <select
                                value={editForm.role}
                                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                className="mt-1 h-10 w-full px-3 rounded-md border border-border bg-surface1 text-sm"
                                data-testid="user-edit-role"
                            >
                                <option value="admin">Administrator</option>
                                <option value="user">Benutzer</option>
                                <option value="guest">Gast</option>
                            </select>
                        </div>
                        {editForm.role !== "admin" && (
                            <div>
                                <Label>Verlage</Label>
                                <p className="text-[11px] text-muted-foreground mb-2">
                                    Verlage, die dieser Benutzer sehen / bearbeiten darf.
                                </p>
                                <div className="space-y-2">
                                    {tenants.map((t) => (
                                        <label
                                            key={t.tenant_id}
                                            className="flex items-center gap-2 cursor-pointer"
                                            data-testid={`user-edit-tenant-${t.tenant_id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={editForm.allowed_tenants.includes(t.tenant_id)}
                                                onChange={() => toggleEditTenant(t.tenant_id)}
                                                className="h-4 w-4"
                                                style={{ accentColor: t.farbe }}
                                            />
                                            <span className="text-sm">{t.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        {editForm.role === "admin" && (
                            <div className="text-xs text-muted-foreground italic border-l-2 border-tenant pl-3">
                                Administratoren haben automatisch Zugriff auf alle Verlage.
                            </div>
                        )}
                        <div>
                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-1.5">
                                    <KeyRound className="h-3.5 w-3.5" /> Passwort zurücksetzen (optional)
                                </Label>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditForm({ ...editForm, password: suggestPassword() })}
                                    className="h-7 px-2 text-[11px] gap-1"
                                >
                                    <Sparkles className="h-3 w-3" /> Vorschlag
                                </Button>
                            </div>
                            <Input
                                type="text"
                                value={editForm.password}
                                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                                placeholder="leer lassen = unverändert"
                                data-testid="user-edit-password"
                                className="font-mono"
                            />
                            {editForm.password && <PasswordStrength pw={editForm.password} />}
                            {editForm.password && (
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    Der Nutzer muss dieses Passwort beim nächsten Login ändern.
                                </p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={submitEdit}
                            className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                            data-testid="user-edit-submit"
                        >
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Benutzer wirklich löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">{deleteTarget?.name}</span> ({deleteTarget?.email}) wird endgültig
                            entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="user-delete-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="user-delete-confirm"
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Zugangsdaten-Dialog (einmalig nach dem Anlegen/PW-Reset) */}
            <CredentialsDialog
                state={credsDialog}
                onClose={() => setCredsDialog({ ...credsDialog, open: false })}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Zugangsdaten-Dialog
// ---------------------------------------------------------------------------
function CredentialsDialog({ state, onClose }) {
    const [copied, setCopied] = React.useState("");
    const [acknowledged, setAcknowledged] = React.useState(false);
    const { open, name, email, password, mustChange } = state;

    // Reset Bestätigung, wenn Dialog neu geöffnet wird
    React.useEffect(() => {
        if (open) setAcknowledged(false);
    }, [open]);

    const copy = async (value, key) => {
        const doFallback = () => {
            try {
                const ta = document.createElement("textarea");
                ta.value = value;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.top = "-1000px";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ta.setSelectionRange(0, value.length);
                const ok = document.execCommand("copy");
                document.body.removeChild(ta);
                return ok;
            } catch {
                return false;
            }
        };

        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(value);
                ok = true;
            }
        } catch {
            ok = false;
        }
        if (!ok) ok = doFallback();

        if (ok) {
            setCopied(key);
            setTimeout(() => setCopied(""), 1500);
        } else {
            toast.error("Kopieren nicht möglich – bitte den Text im Feld manuell markieren und mit Strg+C kopieren");
        }
    };

    const kompakt = `E-Mail:   ${email}\nPasswort: ${password}\nLogin:    ${APP_URL}`;

    const mailBody = [
        `Hallo ${name || ""},`,
        "",
        "Ihr Zugang zu INVENTUR PRO wurde eingerichtet. Zum ersten Login:",
        "",
        `  Web-Adresse:  ${APP_URL}`,
        `  E-Mail:       ${email}`,
        `  Passwort:     ${password}`,
        "",
        mustChange
            ? "Aus Sicherheitsgründen werden Sie beim ersten Login aufgefordert, ein eigenes Passwort festzulegen."
            : `Bitte ändern Sie das Passwort nach dem ersten Login unter "Passwort ändern".`
            ,
        "",
        "Bei Fragen stehe ich gerne zur Verfügung.",
        "",
        "Freundliche Grüße",
    ].join("\n");

    const mailtoHref = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent("Ihr Zugang zu INVENTUR PRO")}&body=${encodeURIComponent(mailBody)}`;

    return (
        <Dialog open={open}>
            <DialogContent
                className="max-w-md [&>button.absolute]:hidden"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                data-testid="creds-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <KeyRound className="h-4 w-4" /> Zugangsdaten
                    </DialogTitle>
                    <DialogDescription>
                        <strong>Einmalige Anzeige.</strong> Kopieren Sie die Daten jetzt oder senden Sie sie direkt per E-Mail. Später ist das Passwort nicht mehr sichtbar.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <FieldWithCopy label="E-Mail" value={email} copied={copied === "email"} onCopy={() => copy(email, "email")} />
                    <FieldWithCopy label="Initial-Passwort" value={password} mono copied={copied === "pw"} onCopy={() => copy(password, "pw")} />
                    <FieldWithCopy
                        label="Zugangsdaten kompakt (Text)"
                        value={kompakt}
                        mono
                        multiline
                        copied={copied === "kompakt"}
                        onCopy={() => copy(kompakt, "kompakt")}
                    />
                    {mustChange && (
                        <div className="text-[11px] text-muted-foreground border-l-2 border-yellow-500/50 pl-3">
                            Der Nutzer wird beim ersten Login zur Passwortänderung aufgefordert.
                        </div>
                    )}
                    <label className="flex items-start gap-2 cursor-pointer text-xs bg-yellow-500/10 border border-yellow-500/40 rounded-md px-3 py-2 mt-2">
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                            className="h-4 w-4 mt-0.5"
                            data-testid="creds-ack"
                        />
                        <span className="text-yellow-200/90">
                            Ich habe die Zugangsdaten notiert oder per E-Mail versendet.
                            Nach dem Schließen ist das Passwort nicht mehr abrufbar.
                        </span>
                    </label>
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <a
                        href={mailtoHref}
                        className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md border border-border bg-surface2 hover:bg-surface1 text-sm font-medium"
                        data-testid="creds-mailto"
                    >
                        <Mail className="h-4 w-4" /> E-Mail vorbereiten
                    </a>
                    <Button
                        onClick={onClose}
                        disabled={!acknowledged}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)] disabled:opacity-40"
                        data-testid="creds-close"
                    >
                        Schließen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldWithCopy({ label, value, mono, multiline, copied, onCopy }) {
    return (
        <div>
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</Label>
            <div className="flex items-start gap-2 mt-1">
                {multiline ? (
                    <textarea
                        readOnly
                        value={value}
                        rows={3}
                        className={`flex-1 h-auto rounded-md border border-border bg-surface2 px-3 py-2 text-xs ${mono ? "font-mono" : ""}`}
                    />
                ) : (
                    <Input
                        readOnly
                        value={value}
                        className={mono ? "font-mono flex-1" : "flex-1"}
                    />
                )}
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onCopy}
                    title="Kopieren"
                    className="shrink-0"
                >
                    {copied ? <CheckIcon className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                </Button>
            </div>
        </div>
    );
}
